import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Section, StatusPill, EmptyState } from "@/components/app/Primitives";
import { ReviewConfirmDialog } from "@/components/app/ReviewConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import TID from "@/lib/testIds";
import { Plus, Trash2, Pencil } from "lucide-react";

/**
 * Influencer-side dialog for editing an in-flight deliverable (status:
 * "submitted" or "clarification_requested"). Re-runs the AI classification
 * so the reviewer sees fresh feedback after the fix.
 */
function EditDeliverableDialog({ deliverable, onDone }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState(deliverable.url || "");
  const [caption, setCaption] = useState(deliverable.caption || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setUrl(deliverable.url || "");
      setCaption(deliverable.caption || "");
    }
  }, [open, deliverable.url, deliverable.caption]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/deliverables/${deliverable.id}`,
                      { url, caption }, { timeout: 90000 });
      toast.success("Submission updated — AI reviewed the new content");
      setOpen(false);
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="border-[#0052FF] text-[#0052FF] hover:bg-[#0052FF]/5"
          data-testid={`deliverable-edit-${deliverable.id}`}
        >
          <Pencil size={14} className="mr-1.5" /> Edit submission
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="edit-deliverable-dialog">
        <DialogHeader>
          <DialogTitle>Edit your submission</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-slate-500">
            <div><b className="text-slate-700 not-italic">Campaign:</b> {deliverable.campaign}</div>
            <div><b className="text-slate-700 not-italic">PO line:</b> {deliverable.service_type || "—"}</div>
          </div>
          <div>
            <label className="text-xs text-slate-500 font-semibold uppercase tracking-widest">Link</label>
            <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://instagram.com/reel/…"
                   data-testid="edit-deliverable-url" />
            <p className="text-[10px] text-slate-500 mt-1">Must be an Instagram / YouTube / social hosting URL. Anything else will re-flag as 🔴 Link Invalid.</p>
          </div>
          <div>
            <label className="text-xs text-slate-500 font-semibold uppercase tracking-widest">Caption / notes</label>
            <Textarea rows={4} value={caption} onChange={(e) => setCaption(e.target.value)}
                      data-testid="edit-deliverable-caption" />
            <p className="text-[10px] text-slate-500 mt-1">15+ characters, mostly words — short or gibberish captions re-flag as 🟡 Review Warning.</p>
          </div>
          <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 italic">
            Re-submitting will re-run the AI Enabled Review Feedback checks. You can keep editing until the customer approves or rejects this deliverable.
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button disabled={busy} onClick={save} className="bg-[#0052FF] hover:bg-[#0043D9]"
                    data-testid="edit-deliverable-save">
              {busy ? "AI re-reviewing…" : "Save & re-classify"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Renders the deterministic AI review flag for a deliverable.
 * Levels: red (blocking) | attention | warn | ok.
 */
function AIReviewBadge({ flag }) {
  if (!flag || !flag.level) return null;
  const styles = {
    red:       { bg: "bg-rose-100",    border: "border-rose-300",    text: "text-rose-800",    icon: "🔴" },
    attention: { bg: "bg-amber-100",   border: "border-amber-300",   text: "text-amber-900",   icon: "🟠" },
    warn:      { bg: "bg-yellow-100",  border: "border-yellow-300",  text: "text-yellow-900",  icon: "🟡" },
    ok:        { bg: "bg-emerald-100", border: "border-emerald-300", text: "text-emerald-800", icon: "🟢" },
  }[flag.level] || { bg: "bg-slate-100", border: "border-slate-300", text: "text-slate-700", icon: "•" };
  return (
    <div
      data-testid={`ai-flag-${flag.level}`}
      className={`inline-flex flex-col rounded-md border ${styles.bg} ${styles.border} ${styles.text} px-2.5 py-1.5 not-italic`}
    >
      <div className="flex items-center gap-1.5 font-bold text-[11px] uppercase tracking-widest">
        <span>{styles.icon}</span>
        <span>{flag.label}</span>
      </div>
      {flag.reason && <div className="text-[11px] mt-0.5 opacity-80">{flag.reason}</div>}
    </div>
  );
}

const SUBTITLES = {
  influencer: "Submit one row per PO service line. AI classifies each item and notifies the EY audit team. Final payment is released only after every line is EY-approved.",
  ey: "Review each deliverable on behalf of the FMCG customer. Approve to release payment, ask for clarification, or reject with a note. Every EY action is audit-logged.",
  other: "Approve or reject work uploaded by influencers. AI summarises each item.",
};

function pageSubtitle(isInfluencer, isEYAuditor) {
  if (isInfluencer) return SUBTITLES.influencer;
  return isEYAuditor ? SUBTITLES.ey : SUBTITLES.other;
}

function submitLabel(busy, submitStatus, count) {
  if (busy) return submitStatus ? `AI reviewing ${submitStatus.current}/${submitStatus.total}…` : "Submitting…";
  return count ? `Submit ${count} deliverable${count === 1 ? "" : "s"}` : "Submit deliverables";
}

function emptyRow(idx = null, type = "") {
  return {
    // SEC: stable client-side row id — prevents React reconciliation bugs when
    // rows are added or removed mid-form (index-as-key anti-pattern).
    _key: (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `r_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    service_index: idx,
    service_type: type,
    url: "",
    caption: "",
    file: null,
  };
}

export default function Deliverables() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [open, setOpen] = useState(false);
  const [orderId, setOrderId] = useState("");
  const [progress, setProgress] = useState(null);
  const [rows, setRows] = useState([emptyRow()]);
  const [busy, setBusy] = useState(false);
  // AI-thinking indicator: shows which row is being classified + how many are done
  // out of total, so users never think the "Submitting…" state is stuck.
  const [submitStatus, setSubmitStatus] = useState(null);
  const isInfluencer = user.role === "influencer";
  const isCustomer = user.role === "customer";
  const isEYAuditor = user.role === "ey_auditor";
  const canApprove = isEYAuditor || user.role === "propay_admin";

  const load = useCallback(
    () => api.get("/deliverables").then((r) => setItems(r.data)),
    []
  );
  useEffect(() => {
    load();
    if (isInfluencer) {
      api.get("/orders").then((r) =>
        setOrders(r.data.filter((o) => ["po_issued", "in_progress", "delivered"].includes(o.status)))
      );
    }
  }, [load, isInfluencer]);

  // When an order is chosen, load per-line progress
  useEffect(() => {
    if (!orderId) { setProgress(null); return; }
    api.get(`/orders/${orderId}/deliverable-progress`).then((r) => {
      setProgress(r.data);
      // Reset rows: one row per remaining unit across lines (cap at first row filled)
      const seed = [];
      r.data.lines.forEach((l) => {
        for (let n = 0; n < l.remaining; n++) seed.push(emptyRow(l.service_index, l.type));
      });
      setRows(seed.length ? seed : [emptyRow()]);
    }).catch(() => setProgress(null));
  }, [orderId]);

  const updateRow = (i, patch) => setRows(rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const addRow = () => setRows([...rows, emptyRow(progress?.lines?.[0]?.service_index ?? null, progress?.lines?.[0]?.type ?? "")]);
  const removeRow = (i) => setRows(rows.length > 1 ? rows.filter((_, idx) => idx !== i) : rows);

  const submitAll = async (e) => {
    e.preventDefault();
    if (!orderId) return toast.error("Select an order");
    const valid = rows.filter((r) => r.url || r.file);
    if (!valid.length) return toast.error("Add at least one URL or file");
    setBusy(true);
    let okCount = 0, failCount = 0;
    for (let i = 0; i < valid.length; i++) {
      const r = valid[i];
      // Live "AI thinking" indicator — reassures the user that the wait is
      // real work (Claude classification + deterministic flag checks) and not
      // a stuck screen.
      setSubmitStatus({ current: i + 1, total: valid.length, phase: "uploading",
                        line: r.service_type || `Row ${i + 1}` });
      try {
        // Hard safety timeout so the UI never hangs > 90s per row even if the
        // upstream LLM is unreachable — the backend will still record the
        // deliverable with the deterministic flag; the timeout only prevents
        // the frontend from waiting forever for the AI classification bit.
        if (r.file) {
          const fd = new FormData();
          fd.append("order_id", orderId);
          fd.append("caption", r.caption || "");
          if (r.service_index !== null && r.service_index !== undefined) fd.append("service_index", String(r.service_index));
          if (r.service_type) fd.append("service_type", r.service_type);
          fd.append("file", r.file);
          setSubmitStatus({ current: i + 1, total: valid.length, phase: "ai_classifying",
                            line: r.service_type || `Row ${i + 1}` });
          await api.post("/deliverables/upload", fd, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 90000,
          });
        } else {
          setSubmitStatus({ current: i + 1, total: valid.length, phase: "ai_classifying",
                            line: r.service_type || `Row ${i + 1}` });
          await api.post("/deliverables", {
            order_id: orderId, url: r.url, caption: r.caption,
            service_index: r.service_index ?? null, service_type: r.service_type || null,
          }, { timeout: 90000 });
        }
        okCount++;
      } catch (err) {
        failCount++;
        console.warn(`Deliverable ${i + 1} failed:`, err?.message);
      }
    }
    setSubmitStatus(null);
    setBusy(false);
    if (okCount) toast.success(`${okCount} deliverable${okCount === 1 ? "" : "s"} submitted — AI classified and FMCG notified`);
    if (failCount) toast.error(`${failCount} failed to submit — please retry those rows.`);
    if (okCount) { setOpen(false); setRows([emptyRow()]); setOrderId(""); load(); }
  };

  const decide = async (id, decision, comment = "") => {
    await api.post(`/deliverables/${id}/decision`, { decision, comment });
    toast.success(`Marked ${decision}`);
    load();
  };

  return (
    <div className="space-y-6">
      <Section
        title={isInfluencer ? "Your deliverables" : "Deliverable approvals"}
        subtitle={pageSubtitle(isInfluencer, isEYAuditor)}
        right={isInfluencer ? (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="open-submit-deliverable" className="bg-[#0052FF] hover:bg-[#0043D9]">+ Submit deliverables</Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Submit deliverables against your PO</DialogTitle></DialogHeader>
              <form onSubmit={submitAll} className="space-y-4">
                <div>
                  <label className="text-xs font-semibold">Order</label>
                  <Select value={orderId} onValueChange={setOrderId}>
                    <SelectTrigger><SelectValue placeholder="Select an active order" /></SelectTrigger>
                    <SelectContent>{orders.map((o) => <SelectItem key={o.id} value={o.id}>{o.campaign} — {o.order_number}</SelectItem>)}</SelectContent>
                  </Select>
                </div>

                {/* Progress per line */}
                {progress && progress.lines?.length > 0 && (
                  <div className="border rounded-lg bg-slate-50 p-3 space-y-2">
                    <div className="text-xs font-bold uppercase tracking-widest text-slate-500">PO progress</div>
                    {progress.lines.map((l) => {
                      const pct = l.required ? Math.round((l.approved / l.required) * 100) : 0;
                      return (
                        <div key={l.service_index} className="text-sm">
                          <div className="flex justify-between mb-0.5">
                            <span className="font-semibold">{l.type}</span>
                            <span className="text-slate-600">
                              approved <b className="text-emerald-700">{l.approved}</b> · submitted <b>{l.submitted}</b> / required <b>{l.required}</b>
                            </span>
                          </div>
                          <Progress value={pct} className="h-1.5" />
                        </div>
                      );
                    })}
                    {progress.all_lines_approved && <div className="text-xs text-emerald-700 font-semibold">Every line approved — you can request final payment.</div>}
                  </div>
                )}

                {/* Submission rows */}
                <div className="space-y-3">
                  <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Submissions</div>
                  {rows.map((r, i) => (
                    <div key={r._key} className="border rounded-lg p-3 bg-white space-y-2">
                      <div className="grid grid-cols-12 gap-2 items-center">
                        <div className="col-span-8">
                          <label className="text-xs text-slate-500">PO line</label>
                          <Select
                            value={r.service_index !== null && r.service_index !== undefined ? String(r.service_index) : ""}
                            onValueChange={(v) => {
                              const idx = parseInt(v);
                              const line = progress?.lines?.find((x) => x.service_index === idx);
                              updateRow(i, { service_index: idx, service_type: line?.type || "" });
                            }}
                            disabled={!progress}
                          >
                            <SelectTrigger className="h-9"><SelectValue placeholder={progress ? "Select PO line" : "Pick order first"} /></SelectTrigger>
                            <SelectContent>
                              {progress?.lines?.map((l) => (
                                <SelectItem key={l.service_index} value={String(l.service_index)}>
                                  {l.type} — {l.approved}/{l.required} approved
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-4 flex justify-end">
                          <button type="button" onClick={() => removeRow(i)} className="text-slate-400 hover:text-rose-600 text-sm inline-flex items-center gap-1">
                            <Trash2 size={14} /> Remove
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-slate-500">Link to deliverable (or upload file below)</label>
                        <Input data-testid={`${TID.deliverableUrlInput}-${i}`} placeholder="https://instagram.com/p/…" value={r.url} onChange={(e) => updateRow(i, { url: e.target.value })} />
                      </div>
                      <div className="grid grid-cols-12 gap-2">
                        <div className="col-span-8">
                          <label className="text-xs text-slate-500">Caption / notes</label>
                          <Textarea data-testid={`${TID.deliverableCaptionInput}-${i}`} rows={2} placeholder="What was posted…" value={r.caption} onChange={(e) => updateRow(i, { caption: e.target.value })} />
                        </div>
                        <div className="col-span-4">
                          <label className="text-xs text-slate-500">Or attach a file</label>
                          <input data-testid={`${TID.deliverableFileInput}-${i}`} type="file" onChange={(e) => updateRow(i, { file: e.target.files?.[0] || null })} className="text-xs mt-1" />
                          {r.file && <div className="text-xs text-slate-500 mt-0.5 truncate">{r.file.name}</div>}
                        </div>
                      </div>
                    </div>
                  ))}
                  <button type="button" onClick={addRow} className="text-sm text-[#0052FF] font-semibold inline-flex items-center gap-1">
                    <Plus size={14} /> Add another submission
                  </button>
                </div>

                {/* Live "AI thinking" indicator — shows what the backend is doing so
                    the user knows the wait is real work (Claude LLM + deterministic
                    flag checks), never a stuck screen. */}
                {busy && submitStatus && (
                  <div
                    data-testid="deliverable-ai-progress"
                    className="rounded-lg border border-[#0052FF]/20 bg-[#0052FF]/5 p-3 space-y-2"
                  >
                    <div className="flex items-center gap-3">
                      <div className="relative w-5 h-5">
                        <div className="absolute inset-0 rounded-full border-2 border-[#0052FF]/20"></div>
                        <div className="absolute inset-0 rounded-full border-2 border-t-[#0052FF] border-r-transparent border-b-transparent border-l-transparent animate-spin"></div>
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-slate-900">
                          {submitStatus.phase === "ai_classifying"
                            ? "AI reviewing your submission…"
                            : "Uploading your submission…"}
                        </div>
                        <div className="text-xs text-slate-600">
                          {submitStatus.line} · Row {submitStatus.current} of {submitStatus.total}
                          {submitStatus.phase === "ai_classifying" && (
                            <span className="text-slate-500"> · ProPay Intelligent AI is classifying + running Link/Attachment/Caption checks</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="h-1 rounded bg-slate-200 overflow-hidden">
                      <div
                        className="h-full bg-[#0052FF] transition-all duration-500"
                        style={{ width: `${((submitStatus.current - 1) / submitStatus.total) * 100}%` }}
                      ></div>
                    </div>
                    <div className="text-[10px] text-slate-500 italic">
                      This can take 10–20 seconds per row while our AI classifier reviews the content — please don&apos;t close this window.
                    </div>
                  </div>
                )}

                <Button data-testid={TID.deliverableSubmitBtn} disabled={busy || !orderId} type="submit" className="w-full bg-[#0052FF] hover:bg-[#0043D9]">
                  {submitLabel(busy, submitStatus, rows.filter((r) => r.url || r.file).length)}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        ) : null}
      />

      {items.length === 0 ? (
        <EmptyState title="No deliverables yet" hint={isInfluencer ? "Once you have an active PO, submit each PO line here." : "When influencers upload work you'll approve or reject each line here."} />
      ) : (
        <div className="grid gap-4">
          {items.map((d) => (
            <div key={d.id} className="bg-white rounded-xl border p-5 flex gap-4 flex-wrap">
              <div className="flex-1 min-w-[240px]">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <StatusPill status={d.status} />
                  <span className="text-xs uppercase tracking-widest text-[#0052FF] font-bold">{d.ai?.type?.replace(/_/g, " ")}</span>
                  {d.service_type && <span className="text-[10px] bg-slate-100 px-2 py-0.5 rounded uppercase tracking-widest text-slate-600">PO line: {d.service_type}</span>}
                </div>
                <div className="font-semibold text-slate-900">{d.campaign} · {d.influencer_name}</div>
                <div className="text-sm text-slate-600 mt-1 break-words">{d.caption}</div>
                {d.url && <a href={d.url} target="_blank" rel="noreferrer" className="text-xs text-[#0052FF] underline break-all">{d.url}</a>}
                <div className="text-xs text-slate-500 mt-2 italic">
                  <AIReviewBadge flag={d.ai?.flag} />
                  <div className="mt-1.5">
                    <b className="not-italic text-slate-700">AI Enabled Review Feedback:</b> {d.ai?.summary || "—"}
                    {d.ai?.topics?.length ? <span className="ml-2 text-slate-400">Topics: {d.ai.topics.join(", ")}</span> : null}
                  </div>
                </div>
                {d.decision_comment && <div className="text-xs text-slate-500 mt-1">Decision note: {d.decision_comment}</div>}
              </div>
              {isInfluencer && (d.status === "submitted" || d.status === "clarification_requested") && (
                <div className="flex flex-col gap-2 min-w-[160px]">
                  <EditDeliverableDialog deliverable={d} onDone={load} />
                  <div className="text-[10px] text-slate-500 italic px-1 leading-tight">
                    You can fix the link, caption, or notes until the customer approves or rejects.
                  </div>
                </div>
              )}
              {canApprove && (d.status === "submitted" || d.status === "clarification_requested") && (
                <div className="flex flex-col gap-2 min-w-[160px]">
                  <ReviewConfirmDialog
                    trigger={<Button className="bg-emerald-600 hover:bg-emerald-700">✓ Approve</Button>}
                    title="Approve deliverable?"
                    subtitle="Confirm you've reviewed this deliverable and it meets the campaign brief."
                    details={{
                      Campaign: d.campaign,
                      Influencer: d.influencer_name,
                      "PO line": d.service_type || "—",
                      "Deliverable type": (d.ai?.type || "other").replace(/_/g, " "),
                      Link: d.url ? <a href={d.url} target="_blank" rel="noreferrer" className="text-[#0052FF] underline break-all">{d.url}</a> : "—",
                      "AI Review Feedback": d.ai?.flag?.label ? `${d.ai.flag.label} — ${d.ai?.summary || ""}` : (d.ai?.summary || "—"),
                      Caption: d.caption || "—",
                    }}
                    confirmLabel="Yes, approve"
                    confirmTone="success"
                    requireComment
                    commentLabel="Note to influencer (optional)"
                    onConfirm={(c) => decide(d.id, "approved", c)}
                    testIdOpen={TID.deliverableApprove(d.id)}
                    testIdConfirm={`confirm-approve-${d.id}`}
                  />
                  <ReviewConfirmDialog
                    trigger={<Button variant="outline" className="border-amber-300 text-amber-800 hover:bg-amber-50">? Ask clarification</Button>}
                    title="Ask for clarification"
                    subtitle="The influencer will see your message and can resubmit or explain."
                    details={{
                      Campaign: d.campaign,
                      Influencer: d.influencer_name,
                      "PO line": d.service_type || "—",
                      Link: d.url ? <a href={d.url} target="_blank" rel="noreferrer" className="text-[#0052FF] underline break-all">{d.url}</a> : "—",
                      "AI Review Feedback": d.ai?.flag?.label ? `${d.ai.flag.label} — ${d.ai?.summary || ""}` : (d.ai?.summary || "—"),
                    }}
                    confirmLabel="Send clarification"
                    confirmTone="warning"
                    requireComment commentRequired
                    commentLabel="What do you need clarified?"
                    onConfirm={(c) => decide(d.id, "clarify", c)}
                    testIdOpen={`deliverable-clarify-${d.id}`}
                    testIdConfirm={`confirm-clarify-${d.id}`}
                  />
                  <ReviewConfirmDialog
                    trigger={<Button variant="destructive">✕ Reject</Button>}
                    title="Reject deliverable?"
                    subtitle="Rejection is captured in the audit log and blocks final payment for this line."
                    details={{
                      Campaign: d.campaign,
                      Influencer: d.influencer_name,
                      "PO line": d.service_type || "—",
                      Link: d.url ? <a href={d.url} target="_blank" rel="noreferrer" className="text-[#0052FF] underline break-all">{d.url}</a> : "—",
                      "AI Review Feedback": d.ai?.flag?.label ? `${d.ai.flag.label} — ${d.ai?.summary || ""}` : (d.ai?.summary || "—"),
                      Caption: d.caption || "—",
                    }}
                    confirmLabel="Yes, reject"
                    confirmTone="danger"
                    requireComment commentRequired
                    commentLabel="Reason for rejection"
                    onConfirm={(c) => decide(d.id, "rejected", c)}
                    testIdOpen={TID.deliverableReject(d.id)}
                    testIdConfirm={`confirm-reject-${d.id}`}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
